import { ColumnModel, ColumnType, ColumnValidator } from '@shared/components/beyond-table/model/column.model';
import { TranslateService } from '@ngx-translate/core';
import { Workbook } from 'exceljs';
import { EXCEL_SHEET_NAME } from '@views/secured/beyond-smart-reports/services/flexmonster-properties.service';
import { DataGridCell, exportDataGrid } from 'devextreme/excel_exporter';
import { generateFileName, saveArrayBufferAsFile } from '@shared/util/file.util';
import DxDataGrid from 'devextreme/ui/data_grid';
import { ObjectRestService } from '@base/modules/rest/object/object-rest.service';
import { UserFieldSettingModel } from '@base/modules/rest/bbs-object/model/user-field-setting.model';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { calcTextWidth } from '@shared/util/util';
import { CustomFieldTypesEnum } from '../modules/rest/custom-field/enums/custom-field-types.enum';
import { CustomFieldModel } from '../modules/rest/custom-field/model/custom-field.model';
import { ObjectDefinitionModel } from '../modules/rest/bbs-object/model/object-definition.model';
import { DATE_FORMAT, DATETIME_FORMAT } from './date.util';
import { valueOnPath, valueOnPaths } from './common.util';
import { DEFAULT_NUMBER_OF_DECIMALS } from './number.util';

export const CREATED = 'created';
export const MODIFIED = 'modified';
export const CREATED_BY = 'createdBy';
export const MODIFIED_BY = 'modifiedBy';
export const STATUS = 'status';
export const NAME = 'name';
export const ACTIONS = 'actions';
export const DESCRIPTION = 'description';
export const ACTIVE = 'active';
export const CODE = 'code';
export const LINK_COLOR = '#0D6CCC';
export const UPDATED__ = 'updated__';
export const DELETED__ = 'deleted__';

export const NEW__ = 'new__';

export const objectColumns = (objectDefinition: ObjectDefinitionModel, translate: TranslateService): ColumnType[] => {
  if (!objectDefinition) {
    return [];
  }
  populateObjectColumns(objectDefinition);
  return objectDefinition.columns
    .map(column => {
      const field = objectDefinition.fields?.find(item => item.dbName === column);
      if (!field) {
        return {
          type: CustomFieldTypesEnum.TEXT,
          label: column,
          dbName: column,
          readOnly: true,
        } as CustomFieldModel;
      }
      return field;
    })
    .filter(f => !f.hidden)
    .map((f: CustomFieldModel) => toTableColumn(f))
    .map((c: ColumnType) => {
      if (!c.width) {
        c.width = calculateColumnWidth(c.caption);
      }
      return c;
    });
};

export const calculateColumnWidth = (label: string): number => {
  return calcTextWidth(label, '14px Roboto') + 40;
};

export const toTableColumn = (field: CustomFieldModel): ColumnType => {
  return {
    name: field.dbName,
    dataField: toDataField(field),
    dataType: toColumnDataType(field),
    allowEditing: !field.readOnly,
    format: toColumnFormat(field),
    width: field.columnWidth ? field.columnWidth : null,
    caption: field?.label,
    allowReordering: true,
    allowSorting: field?.sortable,
    calculateCellValue: toCalculateCellValue(field),
    validationRules: [],
    lookup: mapComboFixedColumns(field),
    allowGrouping: true,
    customColor: field.tableCellColor,
    headerFilter: toHeaderFilter(field),
  };
};

const toDataField = (field: CustomFieldModel): string => {
  return field.dbName;
};

const columnValidations = (field: CustomFieldModel): ColumnValidator[] => {
  const validators: ColumnValidator[] = [];
  if (field.mandatory) {
    validators.push({type: 'required'});
  }
  return validators;
};

const toCalculateCellValue = (field: CustomFieldModel): any => {
  if (field.type === CustomFieldTypesEnum.SWITCHER || field.type === CustomFieldTypesEnum.CHECKBOX || field.type === CustomFieldTypesEnum.RADIO_BUTTON) {
    return (item: any) => {
      return !!valueOnPath(item, field.dbName);
    };
  }

  if (!field.lookupObject) {
    return undefined;
  }
  if (field.lookupObject && field.type !== CustomFieldTypesEnum.LOOKUP) {
    return (item: any) => valueOnPath(item, field.dbName);
  }
  return (item: any) => {
    if (!item) {
      return '';
    }
    const value = valueOnPath(item, field.dbName);
    if (value) {
      const mainFieldValue = valueOnPaths(value, field.lookupObject.mainField);
      const secondFieldValue = valueOnPaths(value, field.lookupObject.secondField);
      return [mainFieldValue, secondFieldValue].filter(Boolean).join(' ');
    }
    return '';
  };
};

const toColumnDataType = (field: CustomFieldModel): 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime' => {
  if (field.type === CustomFieldTypesEnum.DATE) {
    return 'datetime';
  }
  if (field.type === CustomFieldTypesEnum.DATE_TIME) {
    return 'datetime';
  }
  if (field.type === CustomFieldTypesEnum.NUMBER || field.type === CustomFieldTypesEnum.DECIMAL_NUMBER) {
    return 'number';
  }
  if (field.type === CustomFieldTypesEnum.SWITCHER || field.type === CustomFieldTypesEnum.CHECKBOX || field.type === CustomFieldTypesEnum.RADIO_BUTTON) {
    return 'boolean';
  }
  return 'string';
};

const toColumnFormat = (field: CustomFieldModel): string => {
  if (field.type === CustomFieldTypesEnum.DATE) {
    return DATE_FORMAT;
  }
  if (field.type === CustomFieldTypesEnum.DATE_TIME) {
    return DATETIME_FORMAT;
  }
  if (field.type === CustomFieldTypesEnum.DECIMAL_NUMBER) {
    const numberOfDecimals = field.numberOfDecimals || DEFAULT_NUMBER_OF_DECIMALS;
    const decimalZeros = '0'.repeat(numberOfDecimals);
    return `,##0.${decimalZeros}`;
  }
  return null;
};

const mapComboFixedColumns = (field: CustomFieldModel): any => {
  if (field.type !== CustomFieldTypesEnum.SELECT) {
    return null;
  }
  return {
    dataSource: field.options,
    valueExpr: 'value',
    displayExpr: 'label',
  };
};

export function groupColumns(columns: ColumnModel[], groupByColumns: string[]): ColumnModel[] {
  if (!groupByColumns) {
    return columns;
  }
  let i = 0;
  for (const groupByColumn of groupByColumns) {
    const column = columns.find(c => c.dataField === groupByColumn);
    if (column) {
      column.groupIndex = i++;
      column.allowGrouping = true;
    }
  }
  return columns;
}

export function exportTableData(component: DxDataGrid, fileName: string): void {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(EXCEL_SHEET_NAME);

  exportDataGrid({
    component: component,
    worksheet,
    autoFilterEnabled: true,
    keepColumnWidths: true,
    loadPanel: {
      enabled: false,
    },
    customizeCell: (options) => {
      exportDataGridCellCustomizationOption(options);
    },
  })
    .then(() => {
      workbook.xlsx.writeBuffer()
        .then((buffer: ArrayBuffer) => {
          saveArrayBufferAsFile(buffer, generateFileName(fileName, '.xlsx'));
        });
    });
}

export function saveChangedColumnWidth(columns: ColumnType[], lastEvent: any, objectRestServiceCreator: ObjectRestServiceCreator, objectDefinition: ObjectDefinitionModel): void {
  let objectRestService: ObjectRestService;

  if (objectDefinition) {
    const inputString = lastEvent.fullName;
    const regex = /columns\[(\d+)\]\.width/;
    const match = inputString.match(regex) as string;
    const columnIndex = parseInt(match[1], 10);
    columns[columnIndex].width = lastEvent.value;

    const columnsReordered: ColumnModel[] = objectDefinition.columns
      .map(col => columns.find(c => c.dataField === col))
      .filter(found => !!found);

    const userFieldSettings: UserFieldSettingModel[] = columnsReordered
      .map((column, i) => ({
        fieldName: column.dataField,
        columnWidth: column.width,
        columnDisplayOrder: i + 1,
      }));
    objectRestService = objectRestServiceCreator.create(objectDefinition.name);
    objectRestService.saveColumns({fields: userFieldSettings, setForAll: false})
      .subscribe(objDef => {
        objectDefinition.columns = objDef.columns;
        objectDefinition.fields.forEach((field) => {
          const foundField = objDef.fields.find(value => value.dbName === field.dbName);
          field.columnWidth = foundField?.columnWidth;
        });
      });
  }
}

export function saveChangedColumnReordering(columns: ColumnModel[], event: any, objectRestServiceCreator: ObjectRestServiceCreator, objectDefinition: ObjectDefinitionModel): void {
  const reorderedColumnNames = event ? reorderColumnsIndexes(objectDefinition.columns, event.previousValue, event.value) : objectDefinition.columns;
  const columnsReordered: ColumnModel[] = reorderedColumnNames
    .map(col => columns.find(c => c.dataField === col))
    .filter(found => !!found);
  const userFieldSettings: UserFieldSettingModel[] = columnsReordered
    .map((col, i) => ({
      fieldName: col.dataField,
      columnDisplayOrder: i + 1,
      columnWidth: col.width,
    }));
  objectRestServiceCreator.create(objectDefinition.name).saveColumns({fields: userFieldSettings, setForAll: false})
    .subscribe(objDef => {
      objectDefinition.columns = objDef.columns;
      objectDefinition.fields.forEach((field) => {
        const foundField = objDef.fields.find(value => value.dbName === field.dbName);
        field.columnDisplayOrder = foundField.columnDisplayOrder;
      });
    });
}

function reorderColumnsIndexes(columnNames: string[], oldIndex: number, newIndex: number): string[] {
  const [colToMove]: string[] = columnNames.splice(oldIndex, 1);
  columnNames.splice(newIndex, 0, colToMove);
  return columnNames;
}

function exportDataGridCellCustomizationOption(options: any): void {
  const gridCell: DataGridCell = options.gridCell;
  const excelCell = options.excelCell;

  // Style the entire table
  excelCell.font = {size: 9, name: 'Calibri'};
  excelCell.alignment = {vertical: 'middle'};
  if (gridCell.column.alignment) {
    excelCell.alignment.horizontal = gridCell.column.alignment;
  }
  excelCell.format = gridCell.column.format;

  // Style header row
  if (gridCell.rowType === 'header') {
    excelCell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'd0cece'}};
    excelCell.alignment = {horizontal: 'left', vertical: 'middle'};
    excelCell.font.bold = true;
  }

  // Style group row
  if (gridCell.rowType === 'group') {
    excelCell.fill = {type: 'pattern', pattern: 'solid', bgColor: {argb: 'e9e9e9'}};
    excelCell.font.bold = true;
    excelCell.alignment = {horizontal: 'left', vertical: 'middle'};
  }

  // Style total summary row
  if (gridCell.rowType === 'totalFooter' && excelCell.value) {
    excelCell.font = {size: 9, bold: true, italic: true};
  }
}

export function populateObjectColumns(objectDefinition: ObjectDefinitionModel): void {
  if (!objectDefinition) {
    return;
  }
  if (objectDefinition?.columns?.length) {
    return;
  }
  if (!objectDefinition?.fields?.length) {
    return;
  }
  objectDefinition.columns = objectDefinition.fields
    .filter(field => field.visible)
    .map(field => field.dbName);
}

export function handleColumnFilterValues(column: ColumnModel, actionName: string, columnHeader: HTMLElement): void {
  if (columnHeader) {
    const isHeaderCellsStyle = columnHeader.classList.contains('cell-header-text-style');
    const isHeaderFiltersExist = !!column?.filterValues || !!column?.filterValue;
    if (!isHeaderCellsStyle && column[actionName] !== null) {
      columnHeader.classList.add('cell-header-text-style');
    }
    if (isHeaderCellsStyle && !isHeaderFiltersExist) {
      columnHeader.classList.remove('cell-header-text-style');
    }
  }
}

const toHeaderFilter = (field: CustomFieldModel): any => {
  if (field.type === CustomFieldTypesEnum.DECIMAL_NUMBER || field.type === CustomFieldTypesEnum.NUMBER) {
    return {
      search: {
        enabled: true,
      },
      dataSource: function (data: any) {
        const originalPostProcess = data.dataSource.postProcess;
        data.dataSource.postProcess = function (results: any[]) {
          // Call original postProcess if it exists
          if (originalPostProcess) {
            results = originalPostProcess.call(this, results);
          }

          // Enhance each result item to be searchable with both comma and dot decimal separators
          results.forEach((item: any, index: number) => {
            if (item.text && typeof item.text === 'string') {
              const originalText = item.text;

              // If the text contains a dot (internal format), also make it searchable with comma
              if (originalText.includes('.')) {
                const withComma = originalText.replace(/\./g, ',');
                item.searchText = `${originalText} ${withComma}`;
              } else if (originalText.includes(',')) {
                const withDot = originalText.replace(/,/g, '.');
                item.searchText = `${originalText} ${withDot}`;
              }
            }
          });

          return results;
        };

        // Override the load method to implement custom search logic
        const originalLoad = data.dataSource.load;

        data.dataSource.load = function (loadOptions: any) {
          // If there's a search value, modify loadOptions to load ALL data
          if (loadOptions && loadOptions.searchValue) {
            // Create new loadOptions that will load all data
            const modifiedLoadOptions = {
              ...loadOptions,
              skip: 0,           // Start from beginning
              take: undefined,   // Remove pagination limit
              requireTotalCount: true,  // Ensure we get total count
            };

            // Call original load with modified options to get ALL data
            return originalLoad.call(this, modifiedLoadOptions).then((result: any) => {

              const searchValue = loadOptions.searchValue.toLowerCase();

              // Handle both array and object with data property
              let dataToFilter;
              let isResultArray = false;

              if (Array.isArray(result)) {
                dataToFilter = result;
                isResultArray = true;
              } else if (result && result.data && Array.isArray(result.data)) {
                dataToFilter = result.data;
              } else {
                return result;
              }

              const filteredData = dataToFilter.filter((item: any) => {
                const searchText = (item.searchText || item.text || '').toLowerCase();
                return searchText.includes(searchValue);
              });

              // Return in the same format as received
              if (isResultArray) {
                return filteredData;
              } else {
                return {
                  ...result,
                  data: filteredData,
                  totalCount: filteredData.length,  // Update total count
                };
              }
            });
          }
          return originalLoad.call(this, loadOptions);
        };
        data.dataSource.useDefaultSearch = false;
      },
    };
  }

  return {
    search: {
      enabled: true,
    },
  };
};
